import type { SystemIntegrationsReadmodel } from '@bett3r-dev/teselly-domain';
import { requestOAuthAuthorizationUrl, type SystemsIntegrationsWebhooksGet } from '../../shared/state/oauthAuthorizationUrl';
import { isIntegrationCredentialRevoked } from '../../shared/state/revokedIntegrations';

/**
 * TV1-2446 slice 6 (design D4, UI half) — a channel whose credential the provider revoked.
 *
 * `credentialState` is projected by systems-integrations and is ABSENT for every integration that
 * was never revoked (a restoration removes it again), so only an explicit `'revoked'` counts —
 * see `isIntegrationCredentialRevoked`, the one predicate every call site shares. Actions the
 * server refuses on such a channel (import, import preview, retry of a channel batch) are
 * disabled in the UI with a Reconnect call to action.
 */
export const isChannelCredentialRevoked = ( channel: Pick<SystemIntegrationsReadmodel, 'credentialState'> | null | undefined ): boolean =>
  !!channel && isIntegrationCredentialRevoked( channel );

/**
 * Where the Reconnect call to action points: the channel detail page, which hosts the Reconnect
 * action. The integration id is ONE path segment.
 */
export const channelReconnectPath = ( integrationId: string ): string =>
  `/integrations/channels/${ encodeURIComponent( integrationId ) }`;

/**
 * TV1-2446 slice 5 (design D5) — start a Reconnect: the same OAuth flow Add channel opens
 * (a new tab on the provider's authorize page), but naming the seller this channel belongs to.
 * The server keeps that expectation in the OAuth state, and the callback refuses any other seller
 * without writing anything. No `site` is sent: the server never reads it (the MercadoLibre
 * connector authorizes on MLA). A failed request throws to the global error handler.
 */
export const startChannelReconnect = async (
  webhooks: SystemsIntegrationsWebhooksGet,
  channel: Pick<SystemIntegrationsReadmodel, 'connectorId' | 'integrationUserId'>,
  openWindow: ( url: string, target: string, features: string ) => unknown = ( url, target, features ) => window.open( url, target, features )
): Promise<void> => {
  const url = await requestOAuthAuthorizationUrl( webhooks, {
    connectorId: channel.connectorId,
    expectedIntegrationUserId: channel.integrationUserId
  });
  if ( url ) openWindow( url, '_blank', 'noopener,noreferrer' );
};
